/**
 * Typed fetch wrapper for the FastAPI backend.
 *
 * - Cookies: `credentials: "include"` (server-side session cookie `crabond_session`).
 * - CSRF: every non-GET/HEAD request sends `X-Requested-With: crabond` (the backend rejects
 *   mutations without it).
 * - Tenancy: when a platform admin has selected a tenant, `X-Tenant-Id` is sent on every call.
 * - Errors: non-2xx responses become `ApiError` carrying the RFC 9457 problem+json body.
 *
 * Use the generated types from `./schema` (run `npm run gen:api`):
 *   const me = await api.get<Schemas["MeOut"]>("/api/auth/me");
 */
import type { components } from "./schema";

export type Schemas = components["schemas"];

export interface Problem {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: unknown;
  [key: string]: unknown;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: Problem;

  constructor(problem: Problem) {
    super(problem.detail ?? problem.title ?? `HTTP ${problem.status}`);
    this.name = "ApiError";
    this.status = problem.status;
    this.problem = problem;
  }
}

export const CSRF_HEADER = "X-Requested-With";
export const CSRF_VALUE = "crabond";
export const TENANT_HEADER = "X-Tenant-Id";

let currentTenantId: number | null = null;

/** Platform admins: act inside this tenant (null = home tenant). */
export function setTenantId(id: number | null): void {
  currentTenantId = id;
}

export function getTenantId(): number | null {
  return currentTenantId;
}

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: string;
  query?: Record<string, QueryValue | QueryValue[]>;
  body?: unknown;
  formData?: FormData;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: RequestOptions["query"]): string {
  if (!query) return path;
  const params = new URLSearchParams();
  for (const [key, raw] of Object.entries(query)) {
    const values = Array.isArray(raw) ? raw : [raw];
    for (const v of values) {
      if (v !== undefined && v !== null) params.append(key, String(v));
    }
  }
  const qs = params.toString();
  return qs ? `${path}${path.includes("?") ? "&" : "?"}${qs}` : path;
}

async function toApiError(res: Response): Promise<ApiError> {
  const ctype = res.headers.get("content-type") ?? "";
  if (ctype.includes("json")) {
    try {
      const body = (await res.json()) as Partial<Problem>;
      return new ApiError({ ...body, status: body.status ?? res.status });
    } catch {
      // fall through to a generic problem
    }
  }
  return new ApiError({ status: res.status, title: res.statusText || "Request failed" });
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = (opts.method ?? "GET").toUpperCase();
  const headers = new Headers(opts.headers);
  headers.set("Accept", "application/json, application/problem+json");
  if (method !== "GET" && method !== "HEAD") headers.set(CSRF_HEADER, CSRF_VALUE);
  if (currentTenantId !== null) headers.set(TENANT_HEADER, String(currentTenantId));

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData; // browser sets the multipart boundary
  } else if (opts.body !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(opts.body);
  }

  const res = await fetch(buildUrl(path, opts.query), {
    method,
    headers,
    body,
    credentials: "include",
    signal: opts.signal,
  });
  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  const ctype = res.headers.get("content-type") ?? "";
  if (ctype.includes("json")) return (await res.json()) as T;
  return (await res.blob()) as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions["query"], signal?: AbortSignal) =>
    request<T>(path, { query, signal }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: "PUT", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T = void>(path: string) => request<T>(path, { method: "DELETE" }),
  upload: <T>(path: string, formData: FormData) =>
    request<T>(path, { method: "POST", formData }),
};

/** True when the error is an HTTP status the UI treats specially (404 = not found / not loaded). */
export function isStatus(err: unknown, ...statuses: number[]): err is ApiError {
  return err instanceof ApiError && statuses.includes(err.status);
}

/** Human-readable message for any thrown value (problem+json detail when available). */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const detail = err.problem.detail ?? err.problem.title;
    if (Array.isArray(err.problem.errors) && err.problem.errors.length > 0) {
      const first = err.problem.errors[0] as { msg?: string; loc?: unknown[] };
      const where = Array.isArray(first?.loc) ? first.loc.slice(1).join(".") : "";
      return `${detail ?? "Invalid request"}${first?.msg ? `: ${where ? `${where} — ` : ""}${first.msg}` : ""}`;
    }
    return detail ?? `HTTP ${err.status}`;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

function filenameFrom(res: Response, fallback: string): string {
  const cd = res.headers.get("content-disposition") ?? "";
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd);
  if (star?.[1]) return decodeURIComponent(star[1]);
  const plain = /filename="?([^";]+)"?/i.exec(cd);
  return plain?.[1] ?? fallback;
}

/**
 * Download a file endpoint (memo PDF/DOCX, Excel export).
 *
 * A plain link is preferred because in prod `/api/files` answers with a 307 to a presigned
 * Tigris URL, which `fetch` cannot follow cross-origin without CORS. But a link cannot carry
 * `X-Tenant-Id`, so a platform admin acting inside another tenant downloads through fetch
 * instead (local storage streams, so that path works wherever tenant switching is used).
 */
export async function downloadFile(path: string, fallbackName = "download"): Promise<void> {
  if (currentTenantId === null) {
    const a = document.createElement("a");
    a.href = path;
    a.rel = "noopener";
    // Without `download`, an inline response (the memo's HTML) replaced the app in the tab. The
    // attribute only applies same-origin; a presigned redirect still saves via its own headers.
    a.download = fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    return;
  }
  const headers = new Headers();
  headers.set(TENANT_HEADER, String(currentTenantId));
  const res = await fetch(path, { headers, credentials: "include" });
  if (!res.ok) throw await toApiError(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filenameFrom(res, fallbackName);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

/** Fetch a text/HTML endpoint (memo preview) with the session and tenant headers. */
export async function fetchText(path: string): Promise<string> {
  const headers = new Headers({ Accept: "text/html, text/plain, */*" });
  if (currentTenantId !== null) headers.set(TENANT_HEADER, String(currentTenantId));
  const res = await fetch(path, { headers, credentials: "include" });
  if (!res.ok) throw await toApiError(res);
  return res.text();
}

export type Me = Schemas["MeOut"];
export type Tenant = Schemas["TenantOut"];
export type Job = Schemas["JobOut"];
